import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Collapse,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ChevronDown } from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { formatEur, formatTime, formatDuration } from "./transportUtils";
import { OptionTypeIcon } from "./OptionTypeIcon";
import { TransportStepRow } from "./TransportStepRow";

/* ── Stops indicator ────────────────────────────────────────────────── */

const StopsIndicator = ({ count }) => {
  const label =
    count === 0
      ? "Suora"
      : count === 1
        ? "1 segmenttiä"
        : `${count} segmenttiä`;

  return (
    <Typography
      variant="caption"
      sx={{
        color: count === 0 ? "success.main" : "text.secondary",
        fontWeight: count === 0 ? 600 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  );
};

/* ── Main component ─────────────────────────────────────────────────── */

export const TransportOptionCard = ({ option, isSelected, onSelect, onOptionModified }) => {
  const muiTheme = useMuiTheme();
  const { isDark } = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [showSteps, setShowSteps] = useState(false);

  // Track which legs are disabled — keyed by leg index
  const [disabledLegs, setDisabledLegs] = useState(new Set());

  const hasLegs = option.legs && option.legs.length > 0;

  // Bus-transport and wait legs are the core and cannot be disabled
  const canToggleLeg = useCallback(
    (leg) => leg.type !== "bus-transport" && leg.type !== "wait",
    []
  );

  // Calculate adjusted price based on disabled legs
  const adjustedPrice = useMemo(() => {
    if (!hasLegs || disabledLegs.size === 0) return option.price;

    let deduction = 0;
    option.legs.forEach((leg, idx) => {
      if (disabledLegs.has(idx) && leg.price != null) {
        deduction += leg.price;
      }
    });

    return Math.max(0, option.price - deduction);
  }, [option.price, option.legs, disabledLegs, hasLegs]);

  const priceChanged = adjustedPrice !== option.price;

  const handleToggleSteps = (e) => {
    e.stopPropagation();
    onSelect(e);
    if (hasLegs) setShowSteps((prev) => !prev);
  };

  const handleToggleLeg = useCallback((legIndex) => {
    setDisabledLegs((prev) => {
      const next = new Set(prev);
      if (next.has(legIndex)) {
        next.delete(legIndex);
      } else {
        next.add(legIndex);
      }
      return next;
    });
  }, []);

  // Build modified option for parent consumption
  const getModifiedOption = useCallback(() => {
    const enabledLegs = option.legs
      ? option.legs.filter((_, idx) => !disabledLegs.has(idx))
      : [];

    return {
      ...option,
      price: adjustedPrice,
      legs: enabledLegs,
      _disabledLegIndices: [...disabledLegs],
    };
  }, [option, adjustedPrice, disabledLegs]);

  // Notify parent when leg toggle changes
  React.useEffect(() => {
    if (onOptionModified) {
      onOptionModified(getModifiedOption());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledLegs]);

  const departureTime = formatTime(option.start);
  const arrivalTime   = formatTime(option.end);
  const duration      = formatDuration(option.start, option.end);

  return (
    <Card
      onClick={onSelect}
      sx={{
        cursor: "pointer",
        mb: 1.5,
        borderRadius: 2.5,
        border: "2px solid",
        borderColor: isSelected ? "primary.main" : "transparent",
        boxShadow: isSelected ? 2 : "0 1px 3px rgba(0,0,0,0.08)",
        transition: "all 0.15s ease",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
        },
      }}
    >
      <CardContent
        sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}
      >
        {/* ── Header row ── */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 1.5, sm: 2 }}
          onClick={handleToggleSteps}
        >
          {/* Icon */}
          <OptionTypeIcon optionType={option.optionType} size={isMobile ? 36 : 40} />

          {/* Center: times + duration + legs count */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="baseline" spacing={0.75}>
              <Typography
                variant={isMobile ? "body1" : "subtitle1"}
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {departureTime}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                –
              </Typography>
              <Typography
                variant={isMobile ? "body1" : "subtitle1"}
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {arrivalTime}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {duration}
              </Typography>
              {hasLegs && (
                <>
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: "50%",
                      bgcolor: "text.disabled",
                    }}
                  />
                  <StopsIndicator count={option.legs.length} />
                </>
              )}
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", mt: 0.25, maxWidth: "90%" }}
            >
              {option.name}
            </Typography>
          </Box>

          {/* Price */}
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                fontWeight: 800,
                whiteSpace: "nowrap",
                color: priceChanged ? "success.main" : "text.primary",
                transition: "color 0.2s",
              }}
            >
              {formatEur(adjustedPrice)}
            </Typography>
            {priceChanged && (
              <Typography
                variant="caption"
                sx={{
                  textDecoration: "line-through",
                  color: "text.disabled",
                  display: "block",
                  lineHeight: 1,
                }}
              >
                {formatEur(option.price)}
              </Typography>
            )}
          </Box>

          {/* Dropdown chevron */}
          {hasLegs && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                transition: "transform 0.2s, background 0.15s",
                transform: showSteps ? "rotate(180deg)" : "rotate(0deg)",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <ChevronDown size={18} />
            </Box>
          )}
        </Stack>

        {/* ── Collapsible leg details ── */}
        <Collapse in={showSteps}>
          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ ml: { xs: 0, sm: 6.5 } }}>
            {hasLegs &&
              option.legs.map((leg, index) => (
                <TransportStepRow
                  key={index}
                  step={leg}
                  index={index}
                  isLast={index === option.legs.length - 1}
                  isEnabled={!disabledLegs.has(index)}
                  canToggle={canToggleLeg(leg)}
                  onToggle={handleToggleLeg}
                />
              ))}

            {/* Price summary when legs are toggled off */}
            {priceChanged && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: isDark ? "rgba(76,175,80,0.08)" : "success.50",
                  border: "1px solid",
                  borderColor: isDark ? "rgba(76,175,80,0.2)" : "success.200",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Mukautettu hinta
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography
                      variant="caption"
                      sx={{ textDecoration: "line-through", color: "text.disabled" }}
                    >
                      {formatEur(option.price)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: "success.main" }}>
                      {formatEur(adjustedPrice)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};